/**
 * Printer Service
 * API integration for thermal printer configuration and printing
 */

import apiClient from './apiClient';
import {
  PrinterConfig,
  PrinterStatus,
  CreatePrinterConfigData,
  UpdatePrinterConfigData,
  PrintResult,
  TestConnectionResult,
} from '../types/printer';

class PrinterService {
  private baseUrl = '/printers';

  /**
   * Get all printer configs for a provider
   */
  async getByProvider(providerId: number): Promise<PrinterConfig[]> {
    const response = await apiClient.get<PrinterConfig[]>(
      `${this.baseUrl}/provider/${providerId}`
    );
    return response.data ?? [];
  }

  /**
   * Get print status for a provider (network/browser mode)
   */
  async getStatus(providerId: number): Promise<PrinterStatus> {
    const response = await apiClient.get<PrinterStatus>(
      `${this.baseUrl}/provider/${providerId}/status`
    );
    return response.data as PrinterStatus;
  }

  /**
   * Get a specific printer config
   */
  async getById(id: number): Promise<PrinterConfig> {
    const response = await apiClient.get<PrinterConfig>(
      `${this.baseUrl}/${id}`
    );
    return response.data as PrinterConfig;
  }

  /**
   * Create a new printer config
   */
  async create(data: CreatePrinterConfigData): Promise<PrinterConfig> {
    const response = await apiClient.post<PrinterConfig>(
      this.baseUrl,
      data
    );
    return response.data as PrinterConfig;
  }

  /**
   * Update a printer config
   */
  async update(id: number, data: UpdatePrinterConfigData): Promise<PrinterConfig> {
    const response = await apiClient.put<PrinterConfig>(
      `${this.baseUrl}/${id}`,
      data
    );
    return response.data as PrinterConfig;
  }

  /**
   * Delete a printer config
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Test connection to a printer
   */
  async testConnection(id: number): Promise<TestConnectionResult> {
    const response = await apiClient.post<TestConnectionResult>(
      `${this.baseUrl}/${id}/test`
    );
    return response.data as TestConnectionResult;
  }

  /**
   * Print a queue ticket for an appointment
   */
  async printQueueTicket(appointmentId: number): Promise<PrintResult> {
    const response = await apiClient.post<PrintResult>(
      `${this.baseUrl}/print/queue-ticket`,
      { appointmentId }
    );
    return response.data as PrintResult;
  }
}

export const printerService = new PrinterService();
